import type { Tables, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { tryGet, tryUpdate } from "./_shared";

type Profile = Tables<"profiles">;
type ProfileUpdate = TablesUpdate<"profiles">;

type ProfileWithAudit = Profile & {
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
};

export interface ProfileUpdateData {
  full_name?: string | null;
  phone?: string | null;
  avatar_url?: string | null;
  system_notify?: boolean;
}

export const profileService = {
  getDisplayName(userId: string) {
    return tryGet<string | null>(
      `/api/profiles/${encodeURIComponent(userId)}/display-name`,
    );
  },

  getCurrentProfile() {
    return tryGet<Profile>("/api/profiles/me");
  },

  get(userId: string) {
    return tryGet<Profile>(`/api/profiles/${encodeURIComponent(userId)}`);
  },

  getById(userId: string) {
    return tryGet<Profile>(`/api/profiles/${encodeURIComponent(userId)}`);
  },

  getWithAuditInfo(userId: string) {
    return tryGet<ProfileWithAudit>(
      `/api/profiles/${encodeURIComponent(userId)}?withAudit=true`,
    );
  },

  updateOwn(_userId: string, data: ProfileUpdateData) {
    return tryUpdate<Profile>("/api/profiles/me", data);
  },

  updateCurrentProfile(
    data: ProfileUpdateData,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<Profile>("/api/profiles/me/tracked", data);
  },

  update(
    userId: string,
    data: ProfileUpdateData,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<Profile>(`/api/profiles/${encodeURIComponent(userId)}`, data);
  },

  updateNotificationPreferences(
    systemNotify: boolean,
    snapshot: Record<string, unknown>,
    context: ServiceContext,
  ) {
    return profileService.updateCurrentProfile({ system_notify: systemNotify }, snapshot, context);
  },
};

export type { Profile, ProfileUpdate };
